import React from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Tag,
  useColorModeValue,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";

import Carousel from "./Carousal";
import MobileCarousel from "./MobileCarousal";
import Carousal2 from "./Carousal2";
import MobileCarousel2 from "./MobileCarousal2";

function Section({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box py={{ base: 10, md: 14 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 6, md: 8 }}>
          <Heading
            fontSize={{ base: "2xl", md: "3xl" }}
            color={titleColor}
            letterSpacing="tight"
          >
            {title}
          </Heading>
          {subtitle ? (
            <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>
        {children}
      </Container>
    </Box>
  );
}

function InfoCard({ label, text }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const labelColor = useColorModeValue("#315369", "white");
  const bodyColor = useColorModeValue("gray.700", "gray.200");

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="24px"
      p={{ base: 6, md: 7 }}
      boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.08)", "none")}
    >
      <HStack spacing={3} mb={3}>
        <Tag
          size="lg"
          borderRadius="full"
          bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
          color="#DC242C"
          fontWeight="900"
          letterSpacing="0.06em"
        >
          {label}
        </Tag>
      </HStack>
      <Text fontSize={{ base: "md", md: "lg" }} color={bodyColor} textAlign="left">
        {text}
      </Text>
    </Box>
  );
}

export default function HomePage() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);

  const accent = "#DC242C";
  const heroText = useColorModeValue("#315369", "white");
  const heroSub = useColorModeValue("gray.600", "gray.300");
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(49,83,105,0.10), rgba(220,36,44,0.10))",
    "linear-gradient(135deg, rgba(49,83,105,0.15), rgba(220,36,44,0.15))"
  );
  const softBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");

  return (
    <Box>
      {/* HERO */}
      <Box
        pt={{ base: "140px", md: "140px" }}
        pb={{ base: 10, md: 14 }}
        bg={heroBg}
        borderBottom="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 12 }} alignItems="center">
            <Stack spacing={5}>
              <HStack spacing={2} flexWrap="wrap">
                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
                  border="1px solid"
                  borderColor={softBorder}
                  fontWeight="800"
                >
                  University of Ottawa
                </Tag>
                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
                  color={accent}
                  fontWeight="900"
                >
                  SITE-CAV
                </Tag>
              </HStack>

              <Heading
                color={heroText}
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                lineHeight={1.05}
                letterSpacing="tight"
              >

                SITE-CAV: Secure, Intelligent & Trustworthy Ecosystems for{" "}
                <Box as="span" color={accent}>
                  Connected and Autonomous Vehicles
                </Box>
              </Heading>

              <Text fontSize={{ base: "md", md: "lg" }} color={heroSub} maxW="52ch">
                We advance AI-driven mobility, security-by-design, resilient connectivity, and legally-aware automation
                to enable safer autonomous transportation.
              </Text>

              <HStack spacing={3} flexWrap="wrap">
                <Button
                  as="a"
                  href="/publications"
                  bg={accent}
                  color="white"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Explore Publications
                </Button>
                <Button
                  as="a"
                  href="/seminars"
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                >
                  Seminars
                </Button>
                <Button
                  as="a"
                  href="/contact-us"
                  variant="ghost"
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                  color={heroText}
                  _hover={{ bg: useColorModeValue("rgba(49,83,105,0.08)", "rgba(255,255,255,0.06)") }}
                >
                  Collaborate →
                </Button>
              </HStack>

              {/* Quick highlights */}
              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} pt={2}>
                {[
                  { k: "Focus", v: "Secure CAV Ecosystems" },
                  { k: "Methods", v: "AI + Networking + Testbeds" },
                  { k: "Impact", v: "Safety, Trust, Policy" },
                ].map((x) => (
                  <Box
                    key={x.k}
                    border="1px solid"
                    borderColor={softBorder}
                    bg={useColorModeValue("rgba(255,255,255,0.65)", "rgba(255,255,255,0.04)")}
                    borderRadius="18px"
                    p={4}
                  >
                    <Text fontSize="xs" letterSpacing="0.12em" fontWeight="900" color={heroSub}>
                      {x.k.toUpperCase()}
                    </Text>
                    <Text fontSize="sm" fontWeight="800" color={heroText} mt={1}>
                      {x.v}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Stack>

            {/* Right visual: FIXED IMAGE SIZING HERE */}
            <Box
              borderRadius="28px"
              overflow="hidden"
              border="1px solid"
              borderColor={softBorder}
              bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
              boxShadow={useColorModeValue("0 18px 50px rgba(0,0,0,0.12)", "none")}
            >
              <Image
                src="home-img-1.png"
                alt="SITE-CAV"
                w="100%"
                // UPDATED: Changed fixed height to auto and objectFit to contain
                h="auto"
                objectFit="contain"
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* FEATURED CAROUSEL (framed instead of floating) */}
      <Box py={{ base: 10, md: 12 }}>
        <Container maxW="1200px">
          <Box
            border="1px solid"
            borderColor={softBorder}
            borderRadius="28px"
            overflow="hidden"
            bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
            boxShadow={useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none")}
          >
            <Box p={{ base: 4, md: 6 }}>
              <Heading fontSize={{ base: "lg", md: "xl" }} color={heroText} letterSpacing="tight">
                Highlights
              </Heading>
              <Text color={heroSub} mt={1}>
                Projects, partnerships, and experimental work from the lab.
              </Text>
            </Box>
            <Divider />
              <Box
                position="relative"
                left="50%"
                right="50%"
                ml="-50vw"
                mr="-50vw"
                w="100vw"
                overflow="hidden"
                py={{ base: 4, md: 6 }}
                bg={useColorModeValue("white", "rgba(255,255,255,0.02)")}
              >
                <Box display="flex" justifyContent="center">
                  {isMobile ? <MobileCarousel /> : <Carousel />}
                </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* MISSION / VISION / GOAL */}
      <Section
        title="Mission, Vision & Goal"
        subtitle=""
      >
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <InfoCard
            label="Mission"
            text={
              <>
                <b>SITE-CAV</b> aims to enable the seamless integration of connected and autonomous vehicles (CAVs)
                into Ontario’s evolving transportation ecosystem by advancing research in secure, intelligent, and
                legally-aware decision-making systems. Through interdisciplinary collaboration, cutting-edge
                experimental testbeds, and industry partnerships, the project develops resilient communication
                protocols, security-by-design frameworks, and next-generation mobility solutions.
              </>
            }
          />
          <InfoCard
            label="Vision"
            text={
              <>
                The vision of SITE-CAV is to pioneer a secure, efficient, and trustworthy ecosystem for CAVs, where
                intelligent transportation systems adapt to technological advancements and societal needs. By bridging
                AI-driven decision support, ultra-fast connectivity, and legal-aware automation, the project aspires to
                set new global standards in autonomous mobility and smart infrastructure.
              </>
            }
          />
          <InfoCard
            label="Goal"
            text={
              <>
                SITE-CAV&apos;s primary goal is to develop, test, and commercialize innovative solutions that enhance
                the safety, reliability, and efficiency of CAVs within complex urban and intercity environments. By
                leveraging state-of-the-art research, experimental testbeds, and industry-driven insights, the project
                aims to shape policies, optimize supply chains, and strengthen cybersecurity measures for the future of
                autonomous transportation.
              </>
            }
          />
        </SimpleGrid>
      </Section>

      <Divider borderColor={softBorder} />

      {/* WHAT WE DO */}
      <Section
        title="What We Do"
        subtitle="Core research themes that define SITE-CAV’s technical direction."
      >
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
          {[
            {
              t: "Secure-by-Design CAVs",
              d: "Threat modeling, resilience, and safety constraints from day one.",
            },
            {
              t: "AI Decision Support",
              d: "Predictive perception, risk estimation, and trustworthy autonomy.",
            },
            {
              t: "Ultra-Fast Connectivity",
              d: "V2X, edge-cloud, NFV orchestration, and robust networking.",
            },
            {
              t: "Legal-Aware Automation",
              d: "Policy-aligned behaviors and compliance-aware system design.",
            },
          ].map((x) => (
            <Box
              key={x.t}
              border="1px solid"
              borderColor={softBorder}
              borderRadius="22px"
              p={6}
              bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
              boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
            >
              <Text fontWeight="900" color={heroText} fontSize="lg" mb={2}>
                {x.t}
              </Text>
              <Text color={heroSub} fontSize="md">
                {x.d}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      {/* SECOND CAROUSEL */}
      <Box pb={{ base: 14, md: 16 }}>
        <Container maxW="1200px">
          <Box
            border="1px solid"
            borderColor={softBorder}
            borderRadius="28px"
            overflow="hidden"
            bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
            boxShadow={useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none")}
          >
            <Box p={{ base: 4, md: 6 }}>
              <Heading fontSize={{ base: "lg", md: "xl" }} color={heroText} letterSpacing="tight">
                Lab Pictures
              </Heading>
              <Text color={heroSub} mt={1}>
                Demo videos, testbeds, and research updates.
              </Text>
            </Box>
            <Divider />
              <Box
                position="relative"
                left="50%"
                right="50%"
                ml="-50vw"
                mr="-50vw"
                w="100vw"
                overflow="hidden"
                py={{ base: 4, md: 6 }}
                bg={useColorModeValue("white", "rgba(255,255,255,0.02)")}
              >
              {isMobile ? <MobileCarousel2 /> : <Carousal2 />}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FINAL CTA */}
      <Box
        py={{ base: 12, md: 16 }}
        bg={useColorModeValue("rgba(49,83,105,0.06)", "rgba(255,255,255,0.03)")}
        borderTop="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            justify="space-between"
          >
            <Stack >
              <Heading fontSize={{ base: "2xl", md: "3xl" }} color={heroText}>
                Want to collaborate?
              </Heading>
              <Text color={heroSub} maxW="70ch">
                We welcome collaborations with academia, industry, and public-sector partners in connected mobility,
                cybersecurity, edge networking, and intelligent transportation systems.
              </Text>
            </Stack>
            <HStack flexWrap="wrap">
              <Button
                as="a"
                href="/contact-us"
                bg={accent}
                color="white"
                fontWeight="900"
                size="lg"
                borderRadius="16px"
                _hover={{ filter: "brightness(0.95)" }}
              >
                Contact the Lab
              </Button>
              <Button
                as="a"
                href="/about-us"
                variant="outline"
                borderColor={softBorder}
                size="lg"
                borderRadius="16px"
                fontWeight="900"
              >
                Meet the Team
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}